import React, { useState, useEffect, useMemo } from 'react'
import { Search, ChevronDown, ChevronRight, X } from 'lucide-react'
import useRegionStore from '@/context/RegionStore'
import useCountryDetailsStore from '@/context/CountryDetailsStore'
import { CountryInfo, CountryGroup } from '@/services/api'
import useAvailableRegionsStore from '@/context/AvailableRegionsStore'
import { logger } from '@/utils/logger'
import { getCountryFlag } from '@/utils/regionDetection'

const CountryMismatchModal: React.FC = () => {
    const [expandedGroups, setExpandedGroups] = useState<string[]>([])
    const [searchQuery, setSearchQuery] = useState('')
    // const userMadeChoiceRef = useRef<boolean>(false)

    const { storeRegion, detectedRegion, chosenRegion, showRegionModal, setShowRegionModal, setChosenRegion, setAcceptedRegion } = useRegionStore()
    const { getRegionInfo, countries } = useCountryDetailsStore()
    const { isRegionAvailable } = useAvailableRegionsStore()

    // Lock body scroll when modal is open
    useEffect(() => {
        if (showRegionModal) {
            const originalStyle = window.getComputedStyle(document.body).overflow
            document.body.style.overflow = 'hidden'
            return () => {
                document.body.style.overflow = originalStyle
            }
        }
    }, [showRegionModal])

    // Get country info for store region
    const storeRegionInfo = storeRegion ? getRegionInfo(storeRegion) : null
    const storeCountryName = storeRegionInfo?.name || storeRegion?.toUpperCase() || ''
    const storeCountryFlag = storeRegionInfo?.flag || ''

    // Get country info for detected region
    const detectedRegionInfo = detectedRegion ? getRegionInfo(detectedRegion) : null
    const detectedCountryName = detectedRegionInfo?.name || detectedRegion?.toUpperCase() || ''
    const detectedCountryFlag = detectedRegionInfo?.flag || (detectedRegion ? getCountryFlag(detectedRegion) : '')

    // Determine scenario
    const isDetectedRegionAvailable = detectedRegion && countries ? Object.entries(countries).some(([key, value]) => {
        if (key.toLowerCase() === detectedRegion.toLowerCase()) return true
        if ('type' in value && value.type === 'group') {
            return detectedRegion.toLowerCase() in value.countries
        }
        return false
    }) : false

    const isStoreRegionAvailable = storeRegion && countries ? Object.entries(countries).some(([key, value]) => {
        if (key.toLowerCase() === storeRegion.toLowerCase()) return true
        if ('type' in value && value.type === 'group') {
            return storeRegion.toLowerCase() in value.countries
        }
        return false
    }) : false

    // Scenario 1: Both regions available and different
    const isScenario1 = isDetectedRegionAvailable && isStoreRegionAvailable
    // Scenario 2: At least one region is not available
    const isScenario2 = !isDetectedRegionAvailable || !isStoreRegionAvailable

    const handleDetectedRegionClick = () => {
        logger.info('component', 'CountryMismatchModal: User selected detected region', {
            from: storeRegion,
            to: detectedRegion,
            timestamp: new Date().toISOString()
        })
        // Close modal FIRST before changing regions to prevent reopening
        setShowRegionModal(false)
        // Mark this region as accepted so we don't keep showing the modal for it
        setAcceptedRegion(detectedRegion || storeRegion)
        // Then change the region
        setChosenRegion(detectedRegion || storeRegion)
    }

    const handleStoreRegionClick = () => {
        logger.info('component', 'CountryMismatchModal: User selected to stay in store region', {
            region: storeRegion,
            timestamp: new Date().toISOString()
        })
        // Close modal FIRST to prevent reopening
        setShowRegionModal(false)
        // Mark this region as accepted
        setAcceptedRegion(storeRegion)
        // Set chosen region to store region (this will trigger URL update if needed)
        setChosenRegion(storeRegion)
    }

    const handleCountrySelect = (region: string) => {
        logger.info('component', 'CountryMismatchModal: User selected region from list', {
            region,
            timestamp: new Date().toISOString()
        })
        setChosenRegion(region)
        setShowRegionModal(false)
    }

    const toggleGroup = (groupName: string, event: React.MouseEvent) => {
        event.stopPropagation()
        setExpandedGroups(prev =>
            prev.includes(groupName)
                ? prev.filter(g => g !== groupName)
                : [...prev, groupName]
        )
    }

    // Separate countries and groups
    const individualCountries: [string, CountryInfo][] = []
    const groups: [string, CountryGroup][] = []

    if (countries) {
        Object.entries(countries).forEach(([key, value]) => {
            if ('type' in value && value.type === 'group') {
                groups.push([key, value])
            } else {
                individualCountries.push([key, value as CountryInfo])
            }
        })
    }

    // Filter countries based on search query
    const { filteredIndividualCountries, filteredGroups } = useMemo(() => {
        if (!countries || !searchQuery.trim()) {
            return {
                filteredIndividualCountries: individualCountries,
                filteredGroups: groups
            }
        }

        const query = searchQuery.toLowerCase()
        const matchesSearch = (name: string) => name.toLowerCase().includes(query)

        const filteredIndividual = individualCountries.filter(([_, info]) =>
            matchesSearch(info.name)
        )

        const filteredGroupsData = groups.map(([groupName, group]) => {
            const filteredCountries = Object.entries(group.countries).filter(([_, info]) =>
                matchesSearch(info.name)
            )
            return [
                groupName,
                { ...group, countries: Object.fromEntries(filteredCountries) }
            ] as [string, CountryGroup]
        }).filter(([_, group]) => Object.keys(group.countries).length > 0)

        return {
            filteredIndividualCountries: filteredIndividual,
            filteredGroups: filteredGroupsData
        }
    }, [countries, searchQuery, individualCountries, groups])

    const renderCountryButton = (code: string, info: CountryInfo) => (
        <button
            key={code}
            onClick={() => handleCountrySelect(code)}
            className="flex items-center gap-2 w-full p-3 hover:bg-[#f4f4f4] transition-colors text-left"
        >
            <span>{info.flag}</span>
            <span className="text-gray-900">{info.name}</span>
        </button>
    )

    // Don't show modal if not requested
    if (!showRegionModal || !detectedRegion) {
        return null
    }

    const showScenario1 = isScenario1
    const showScenario2 = isScenario2

    return (
        <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/50 pointer-events-none">
            <div role="dialog" aria-modal="true" className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col relative z-[99999] pointer-events-auto">
                {/* Scenario 1: Both regions available */}
                {showScenario1 && (
                    <>
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-2xl font-headline text-gray-900">
                                Visiting {storeCountryName}? {storeCountryFlag}
                            </h2>
                            <p className="mt-2 text-gray-600">
                                For shipping to {storeCountryName}, please continue to the {storeCountryName} Store.
                            </p>
                        </div>

                        <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-2 p-4">
                            <button
                                onClick={handleDetectedRegionClick}
                                className="flex items-center justify-between w-full p-4 bg-orange text-white rounded-lg hover:bg-orange/90 transition-colors font-headline"
                            >
                                <span>Shop in {detectedCountryName}</span>
                                <span>{detectedCountryFlag}</span>
                            </button>

                            <button
                                onClick={handleStoreRegionClick}
                                className="flex items-center justify-between w-full p-4 border-2 border-gray-300 text-gray-900 rounded-lg hover:bg-gray-50 transition-colors font-headline"
                            >
                                <span>Continue to {storeCountryName}</span>
                                <span>{storeCountryFlag}</span>
                            </button>
                        </div>
                    </>
                )}

                {/* Scenario 2: At least one region not available */}
                {showScenario2 && (
                    <>
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-2xl font-headline text-gray-900">
                                Region Unavailable {!isStoreRegionAvailable ? storeCountryFlag : detectedCountryFlag}
                            </h2>
                            <p className="mt-2 text-gray-600">
                                {!isStoreRegionAvailable
                                    ? `Sorry but we don't currently ship to ${storeCountryName}. Please select a region where we currently ship to:`
                                    : `Sorry but we don't currently ship to ${detectedCountryName}. Please select a region where we currently ship to:`
                                }
                            </p>
                        </div>

                        <div className="flex-1 overflow-y-auto min-h-0">
                            {filteredIndividualCountries.length === 0 && filteredGroups.length === 0 ? (
                                <div className="flex items-center gap-2 w-full p-3 text-gray-400">
                                    <span>🔍</span>
                                    <span>No countries found</span>
                                </div>
                            ) : (
                                <>
                                    {/* Individual Countries Section */}
                                    {filteredIndividualCountries.length > 0 && (
                                        <div>
                                            {filteredIndividualCountries.map(([code, info]) =>
                                                renderCountryButton(code, info)
                                            )}
                                        </div>
                                    )}

                                    {/* Groups Section */}
                                    {filteredGroups.map(([groupName, group]) => (
                                        <div key={groupName} className="relative">
                                            <button
                                                onClick={(e) => toggleGroup(groupName, e)}
                                                className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50 sticky top-0 bg-white z-10"
                                            >
                                                <span>{groupName}</span>
                                                {(expandedGroups.includes(groupName) || searchQuery) ? (
                                                    <ChevronDown className="w-4 h-4" />
                                                ) : (
                                                    <ChevronRight className="w-4 h-4" />
                                                )}
                                            </button>
                                            {(expandedGroups.includes(groupName) || searchQuery) && (
                                                <div>
                                                    {Object.entries(group.countries).map(([code, info]) =>
                                                        renderCountryButton(code, info)
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>

                        <div className="p-4 border-t border-gray-200">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search countries..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent"
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default CountryMismatchModal
